import type { FC } from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import { CloseButton, TextInput, Drawer, Stack } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { Search as SearchIcon } from 'lucide-react';

import useSearchStore from './Search.store';
import SearchFilters from '@/components/SearchFilters';
import SearchResultsContent from '@/components/SearchResultsContent';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useSearchData } from '@/hooks/useSearchData';
import { useProjectSearchQuery } from '@/services/designer/projects/projects.queries';
import { SEARCH_PARAMS } from '@/const/searchParams';

const DEBOUNCE_TIMEOUT = 500;

const Search: FC = () => {
    const { filters, updateParam } = useSearchFilters();
    const { projects, agents, flows, isLoading } = useSearchData({
        projectId: filters.projectId,
        agentId: filters.agentId
    });

    const { isOpen, close } = useSearchStore();

    const [immediateQuery, setImmediateQuery] = useState(filters.query || '');
    const [debouncedQuery, setDebouncedQuery] = useState(filters.query || '');

    const handleInputKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
            event.preventDefault();
            event.stopPropagation();
            close();
        }
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setDebouncedQuery(value.trim());
        updateParam(SEARCH_PARAMS.QUERY, value.trim() || undefined);
    }, DEBOUNCE_TIMEOUT);

    const { data: searchResults, isLoading: isSearchLoading } =
        useProjectSearchQuery(
            debouncedQuery || undefined,
            {
                project_id: filters.projectId || undefined,
                agent_id: filters.agentId || undefined,
                flow_id: filters.flowId || undefined
            },
            { enabled: !!debouncedQuery }
        );

    useEffect(() => {
        setImmediateQuery(filters.query || '');
        setDebouncedQuery(filters.query || '');
    }, [filters.query]);

    useEffect(() => {
        const hasSearchParams =
            filters.query ||
            filters.projectId ||
            filters.agentId ||
            filters.flowId;

        if (hasSearchParams && !isOpen) {
            useSearchStore.getState().open();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDrawerClose = () => {
        close();
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;
        setImmediateQuery(value);
        debouncedSearch(value);
    };

    const handleClear = () => {
        setImmediateQuery('');
        debouncedSearch('');
    };

    const makeFilterHandler =
        (param: string, reset?: string[]) => (value: string | null) => {
            updateParam(param, value, reset);
        };

    const handleProjectChange = makeFilterHandler(SEARCH_PARAMS.PROJECT_ID, [
        SEARCH_PARAMS.AGENT_ID,
        SEARCH_PARAMS.FLOW_ID
    ]);

    const handleAgentChange = makeFilterHandler(SEARCH_PARAMS.AGENT_ID, [
        SEARCH_PARAMS.FLOW_ID
    ]);

    const handleFlowChange = makeFilterHandler(SEARCH_PARAMS.FLOW_ID);

    return (
        <Drawer
            opened={isOpen}
            position="right"
            size="xl"
            title="Search"
            onClose={handleDrawerClose}
        >
            <Stack>
                <SearchFilters
                    agents={agents}
                    flows={flows}
                    isLoading={isLoading}
                    projects={projects}
                    selectedAgent={filters.agentId}
                    selectedFlow={filters.flowId}
                    selectedProject={filters.projectId}
                    onAgentChange={handleAgentChange}
                    onFlowChange={handleFlowChange}
                    onProjectChange={handleProjectChange}
                />

                <TextInput
                    data-autofocus
                    className="flex-1"
                    label="Search"
                    leftSection={<SearchIcon />}
                    placeholder="Search by idn, title, skill, integration..."
                    rightSection={
                        <CloseButton
                            aria-label="Clear input"
                            style={{
                                display: immediateQuery ? undefined : 'none'
                            }}
                            onClick={handleClear}
                        />
                    }
                    size="xs"
                    value={immediateQuery}
                    onChange={handleChange}
                    onKeyDown={handleInputKeyDown}
                />

                <SearchResultsContent
                    isLoading={isSearchLoading}
                    query={debouncedQuery}
                    searchResults={searchResults}
                />
            </Stack>
        </Drawer>
    );
};

export default Search;
